import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';

import { FilterMode, SortMode } from './types';

export type PipelineToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  filter: FilterMode;
  onFilterChange: (value: FilterMode) => void;
  sort: SortMode;
  onSortChange: (value: SortMode) => void;
  onRefresh: () => void;
  loading?: boolean;
  lastRefreshLabel?: string;
  lastRefreshTooltip?: string;
  /** The week a rerun would process, formatted. Absent when this customer has no manual run. */
  rerunWeekDisplay?: string | null;
  /** True when the function could not be asked, as opposed to answering that there is no run. */
  rerunUnavailable?: boolean;
  rerunning?: boolean;
  onRerun?: () => void;
};

const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'action', label: 'Action needed first' },
  { value: 'complete', label: 'Complete first' },
];

const FILTER_LABELS: Record<Exclude<FilterMode, 'all'>, string> = {
  pending: 'Action needed',
  remits: 'Pending remits',
  complete: 'Complete',
};

const FILTER_CHIP_COLOR: Record<Exclude<FilterMode, 'all'>, 'warning' | 'error' | 'success'> = {
  pending: 'warning',
  remits: 'error',
  complete: 'success',
};

const CONTROL_HEIGHT = 40;

const SortMenu = ({
  sort,
  onSortChange,
}: {
  sort: SortMode;
  onSortChange: (value: SortMode) => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const current = SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0];

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (value: SortMode) => {
    onSortChange(value);
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        onClick={handleClick}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: CONTROL_HEIGHT,
          px: 1.5,
          borderRadius: 1,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          fontSize: 13,
          fontWeight: 500,
          transition: 'background 0.12s ease, border-color 0.12s ease',
          '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'grey.400',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
          ...(open && { backgroundColor: 'grey.50', borderColor: 'grey.400' }),
        }}
      >
        <SortIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
          Sort:
        </Typography>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
          {current.label}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 0.25 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
          },
        }}
      >
        {SORT_OPTIONS.map((opt) => {
          const selected = opt.value === sort;
          return (
            <MenuItem
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              selected={selected}
              sx={{
                fontSize: 13,
                fontWeight: selected ? 600 : 500,
                py: 0.875,
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 18,
                  display: 'inline-flex',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                {selected && <CheckIcon sx={{ fontSize: 16 }} />}
              </Box>
              {opt.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const PipelineToolbar = ({
  search,
  onSearchChange,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  onRefresh,
  loading,
  lastRefreshLabel,
  lastRefreshTooltip,
  rerunWeekDisplay,
  rerunUnavailable,
  rerunning,
  onRerun,
}: PipelineToolbarProps) => {
  const navigate = useNavigate();

  // A rerun always targets the week the schedule would pick, so it belongs to the page rather
  // than to any one card — and the cards are already crowded for multi-carrier customers. Shown
  // disabled when the function cannot be reached: a control that disappears explains nothing.
  const showRerun = Boolean(onRerun) && (Boolean(rerunWeekDisplay) || Boolean(rerunUnavailable));
  const rerunTooltip = rerunWeekDisplay
    ? `Rerun the week of ${rerunWeekDisplay}`
    : 'Cannot reach the service that runs this. Rerun works again once it responds.';
  const activeFilter = filter !== 'all' ? filter : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.25,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        mb: 1.75,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 220,
          maxWidth: 380,
          height: CONTROL_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.5,
        }}
      >
        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        <InputBase
          fullWidth
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder='Search "may 4", "5/4/2026", "04272026"…'
          sx={{ fontSize: 14 }}
        />
      </Box>

      {activeFilter && (
        <Chip
          icon={<FilterAltIcon />}
          label={`Filtered: ${FILTER_LABELS[activeFilter]}`}
          onDelete={() => onFilterChange('all')}
          color={FILTER_CHIP_COLOR[activeFilter]}
          variant="outlined"
          sx={{
            height: CONTROL_HEIGHT,
            fontWeight: 600,
            fontSize: 12.5,
            '& .MuiChip-icon': { fontSize: 16 },
          }}
        />
      )}

      <SortMenu sort={sort} onSortChange={onSortChange} />

      <Box sx={{ flex: 1 }} />

      {lastRefreshLabel && (
        <Tooltip title={lastRefreshTooltip ?? ''} arrow disableHoverListener={!lastRefreshTooltip}>
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            Refreshed {lastRefreshLabel}
          </Typography>
        </Tooltip>
      )}

      {/* Not week-scoped: Vision takes the customer and the week from the file's own contents. */}
      <Button
        variant="outlined"
        onClick={() => navigate('/vision-upload')}
        startIcon={<CloudUploadIcon />}
        sx={{
          height: CONTROL_HEIGHT,
          borderColor: 'grey.300',
          color: 'text.primary',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'grey.400',
          },
        }}
      >
        Upload to Vision
      </Button>

      <Button
        variant="outlined"
        onClick={onRefresh}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        sx={{
          height: CONTROL_HEIGHT,
          borderColor: 'grey.300',
          color: 'text.primary',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'grey.400',
          },
        }}
      >
        Refresh
      </Button>

      {showRerun && (
        <Tooltip title={rerunTooltip} arrow>
          {/* A disabled button fires no mouse events, so the tooltip needs a live wrapper. */}
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Button
              variant="outlined"
              onClick={onRerun}
              disabled={!rerunWeekDisplay || rerunning}
              startIcon={
                rerunning ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />
              }
              sx={{
                height: CONTROL_HEIGHT,
                fontWeight: 600,
                // Colour in the text and border, never a fill: a filled button draws the eye, and
                // this is an action that should be found when wanted rather than noticed in
                // passing. Red because it emails customers and cannot be taken back — the same
                // treatment Clear All gets, which is the other rare, consequential control.
                color: 'error.dark',
                borderColor: '#ef9a9a',
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: '#fdf0ef', borderColor: 'error.main' },
                '&.Mui-disabled': { color: 'text.disabled', borderColor: 'grey.300' },
              }}
            >
              {rerunning ? 'Running…' : 'Rerun this week'}
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
